export type OpenFoodFactsQuantityUnit = "g" | "ml";

export interface OpenFoodFactsProduct {
  code?: string;
  product_name?: string;
  generic_name?: string;
  brands?: string;
  serving_size?: string;
  serving_quantity?: number;
  serving_quantity_unit?: OpenFoodFactsQuantityUnit;
  quantity?: string;
  product_quantity?: number;
  product_quantity_unit?: OpenFoodFactsQuantityUnit;
  image_front_small_url?: string;
  image_front_url?: string;
  nutriments?: Record<string, unknown>;
}

export interface OpenFoodFactsProductResponse {
  status?: number;
  product?: OpenFoodFactsProduct;
}

export interface OpenFoodFactsSearchResponse {
  page?: number;
  page_count?: number;
  page_size?: number;
  count?: number;
  hits: OpenFoodFactsProduct[];
}

export function parseProductResponse(input: unknown): OpenFoodFactsProductResponse {
  const value = asRecord(input);
  return {
    status: optionalNumber(value.status),
    product: value.product === undefined ? undefined : parseProduct(value.product),
  };
}

export function parseSearchResponse(input: unknown): OpenFoodFactsSearchResponse {
  const value = asRecord(input);
  if (!Array.isArray(value.hits)) throw new TypeError("Open Food Facts returned an unexpected search response");
  const hits: OpenFoodFactsProduct[] = [];
  for (const hit of value.hits) {
    try {
      hits.push(parseProduct(hit));
    } catch {
      // A public result set can contain isolated malformed products. Keep the
      // usable hits instead of failing the entire search response.
    }
  }
  return {
    page: optionalNumber(value.page),
    page_count: optionalNumber(value.page_count),
    page_size: optionalNumber(value.page_size),
    count: optionalNumber(value.count),
    hits,
  };
}

function parseProduct(input: unknown): OpenFoodFactsProduct {
  const value = asRecord(input);
  const nutriments = value.nutriments === undefined ? undefined : asRecord(value.nutriments);
  return {
    code: optionalString(value.code),
    product_name: optionalString(value.product_name),
    generic_name: optionalString(value.generic_name),
    brands: optionalStringOrList(value.brands),
    serving_size: optionalString(value.serving_size),
    serving_quantity: optionalPositiveNumber(value.serving_quantity),
    serving_quantity_unit: optionalQuantityUnit(value.serving_quantity_unit),
    quantity: optionalString(value.quantity),
    product_quantity: optionalPositiveNumber(value.product_quantity),
    product_quantity_unit: optionalQuantityUnit(value.product_quantity_unit),
    image_front_small_url: safeHttpsUrl(value.image_front_small_url),
    image_front_url: safeHttpsUrl(value.image_front_url),
    nutriments,
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Open Food Facts returned an unexpected response");
  }
  return value as Record<string, unknown>;
}

function optionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 500) : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const text = value.trim();
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(text)) return undefined;
    const number = Number(text);
    if (Number.isFinite(number)) return number;
  }
  return undefined;
}

function optionalPositiveNumber(value: unknown): number | undefined {
  const number = optionalNumber(value);
  return number !== undefined && number > 0 ? number : undefined;
}

function optionalQuantityUnit(value: unknown): OpenFoodFactsQuantityUnit | undefined {
  if (typeof value !== "string") return undefined;
  const unit = value.normalize("NFKC").trim().toLocaleLowerCase("en-AU");
  return unit === "g" || unit === "ml" ? unit : undefined;
}

function optionalStringOrList(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const values = value.map(optionalString).filter((item): item is string => item !== undefined);
    return values.length > 0 ? values.join(", ").slice(0, 500) : undefined;
  }
  return optionalString(value);
}

function safeHttpsUrl(value: unknown): string | undefined {
  const text = optionalString(value);
  if (!text) return undefined;
  try {
    const url = new URL(text);
    return url.protocol === "https:" ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}
